import { Chip, Tooltip, Typography, useTheme, type Theme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import type { MappingResult } from '@verantos-dev/feature-code-mapping';
import { colors } from '@verantos-dev/shared-ui-components/tokens';

/** DataGrid row = a mapping result plus a stable id. */
export type ConceptGroupMappingRow = MappingResult & { rowId: string };

/** Backend status strings (exact — must match the mapping payload). */
export const MAPPING_STATUS = {
  mapped: 'Mapped',
  unmapped: 'Unmapped',
  notFound: 'Not found',
  alreadyStandard: 'Already standard',
} as const;

/**
 * Status pill colour (same tokens as the legacy grid): Mapped → info, Unmapped →
 * the VDS ``tertiary-orange`` core token (no theme slot for it), Not found →
 * error, Already standard → success.
 */
const statusPillColor = (status: string, theme: Theme): string => {
  switch (status) {
    case MAPPING_STATUS.mapped:
      return theme.palette.info.main;
    case MAPPING_STATUS.unmapped:
      return colors['tertiary-orange'];
    case MAPPING_STATUS.notFound:
      return theme.palette.error.main;
    case MAPPING_STATUS.alreadyStandard:
      return theme.palette.success.main;
    default:
      return theme.palette.grey[500];
  }
};

const statusLabel = (status: string, intl: IntlShape): string => {
  switch (status) {
    case MAPPING_STATUS.mapped:
      return intl.formatMessage({ id: 'codeListMapper.status.mapped' });
    case MAPPING_STATUS.unmapped:
      return intl.formatMessage({ id: 'codeListMapper.status.unmapped' });
    case MAPPING_STATUS.notFound:
      return intl.formatMessage({ id: 'codeListMapper.status.notFound' });
    case MAPPING_STATUS.alreadyStandard:
      return intl.formatMessage({ id: 'codeListMapper.status.alreadyStandard' });
    default:
      return status;
  }
};

/** Plain ellipsised cell — the same body2 treatment the Concepts grid uses. */
const TextCell = ({ value }: { value: ReactNode }) => (
  <Tooltip title={value != null && value !== '' ? String(value) : ''} arrow placement="top">
    <Typography
      variant="body2"
      sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {value}
    </Typography>
  </Tooltip>
);

/**
 * Columns for the concept-group Mapping Results grid, in Figma order:
 * Type · Status · OMOP concept ID · Concept name · Vocabulary · Code · Standard
 * · Domain · Class. Sorting is disabled so the status grouping (Not found →
 * Unmapped → Mapped → Already standard) the panel applies is preserved.
 */
export const useConceptGroupMappingColumns = (): GridColDef<ConceptGroupMappingRow>[] => {
  const theme = useTheme();
  const intl = useIntl();
  const dash = '—';
  const yes = intl.formatMessage({ id: 'epiChat.concepts.standardValue.yes' });
  const no = intl.formatMessage({ id: 'epiChat.concepts.standardValue.no' });

  return useMemo(
    () => [
      {
        field: 'type',
        headerName: intl.formatMessage({ id: 'epiChat.conceptGroupPreview.mapping.col.type' }),
        width: 110,
        sortable: false,
        renderCell: (params) => <TextCell value={params.value} />,
      },
      {
        field: 'status',
        headerName: intl.formatMessage({ id: 'epiChat.conceptGroupPreview.mapping.col.status' }),
        width: 160,
        sortable: false,
        renderCell: (params) => {
          // Nested mapped children carry no status → no pill.
          if (!params.value) return null;
          const label = statusLabel(params.value, intl);
          const rawCount = params.row.mappedCount;
          const count = typeof rawCount === 'number' ? rawCount : Number(rawCount);
          const pillLabel = Number.isFinite(count) ? `${label} (${count})` : label;
          return (
            <Tooltip title={pillLabel} arrow placement="top">
              <Chip
                size="small"
                label={pillLabel}
                sx={{
                  backgroundColor: statusPillColor(params.value, theme),
                  fontWeight: 400,
                  '& .MuiChip-label': { color: theme.palette.common.white },
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'id',
        headerName: intl.formatMessage({
          id: 'epiChat.conceptGroupPreview.mapping.col.omopConceptId',
        }),
        width: 150,
        sortable: false,
        renderCell: (params) => <TextCell value={params.value || dash} />,
      },
      {
        field: 'name',
        headerName: intl.formatMessage({
          id: 'epiChat.conceptGroupPreview.mapping.col.conceptName',
        }),
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => <TextCell value={params.value || dash} />,
      },
      {
        field: 'vocabulary',
        headerName: intl.formatMessage({
          id: 'epiChat.conceptGroupPreview.mapping.col.vocabulary',
        }),
        width: 130,
        sortable: false,
        renderCell: (params) => <TextCell value={params.value || dash} />,
      },
      {
        field: 'code',
        headerName: intl.formatMessage({ id: 'epiChat.conceptGroupPreview.mapping.col.code' }),
        width: 130,
        sortable: false,
        renderCell: (params) => <TextCell value={params.value || dash} />,
      },
      {
        field: 'standard',
        headerName: intl.formatMessage({ id: 'epiChat.conceptGroupPreview.mapping.col.standard' }),
        width: 110,
        sortable: false,
        renderCell: (params) => {
          // Mapped targets + already-standard sources are standard; else not.
          const isStandard =
            params.row.type === 'Mapped' ||
            params.row.status?.toString().toLowerCase() === 'already standard';
          return <TextCell value={isStandard ? yes : no} />;
        },
      },
      {
        field: 'domain',
        headerName: intl.formatMessage({ id: 'epiChat.conceptGroupPreview.mapping.col.domain' }),
        width: 130,
        sortable: false,
        renderCell: (params) => <TextCell value={params.value || dash} />,
      },
      {
        field: 'class',
        headerName: intl.formatMessage({ id: 'epiChat.conceptGroupPreview.mapping.col.class' }),
        width: 160,
        sortable: false,
        renderCell: (params) => <TextCell value={params.value || dash} />,
      },
    ],
    [theme, intl, dash, yes, no]
  );
};
